import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  CircularProgress,
  ClickAwayListener,
  Paper,
  Popper,
  TextField
} from "@material-ui/core";
import { ArrowDropDown as ArrowDropDownIcon } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import Button from "../../waybee-ui/Button";
import SimpleDialog from "../../waybee-ui/Dialog/SimpleDialog";
import {
  crumbLabel,
  fillPath,
  menuTitleCrumbIndex
} from "../../utils/breadcrumbs";
import { menuTitleFromLocation } from "../../utils/dynamicMenu";
import { hasUnsavedChanges } from "../../utils/leaveGuard";
import {
  cachedClassName,
  ensureClassName,
  loadClasses,
  rememberClass
} from "../../utils/classDirectory";

const Nav = styled.nav`
  min-width: 0;
`;

const Trail = styled.ol`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
  list-style: none;
  color: #00303b;
  font-weight: 300;
  font-size: 1.6rem;
  line-height: 1.2;

  li {
    display: inline-flex;
    align-items: center;
    min-width: 0;
    flex: 0 1 auto;
  }

  li:last-child {
    flex: 1 1 auto;
    overflow: hidden;
  }

  li + li::before {
    content: ">";
    margin: 0 10px;
    color: rgba(0, 48, 59, 0.45);
    flex: 0 0 auto;
  }
`;

const CrumbLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const Current = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ClassGroup = styled.span`
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: 320px;
`;

const ClassName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClassNameLink = styled(CrumbLink)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ArrowButton = styled.button`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
`;

const Menu = styled(Paper)`
  width: 320px;
  max-width: 80vw;
  padding: 8px;
`;

const SearchField = styled(TextField)`
  margin-bottom: 8px;
`;

const Options = styled.ul`
  max-height: 280px;
  margin: 0;
  padding: 0;
  overflow: auto;
  list-style: none;
`;

const Option = styled.button`
  display: block;
  width: 100%;
  margin: 0;
  padding: 8px 10px;
  border: 0;
  border-radius: 4px;
  background: ${({ $selected }) =>
    $selected ? "rgba(0, 48, 59, 0.08)" : "transparent"};
  color: #00303b;
  font: inherit;
  font-size: 0.95rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: rgba(0, 48, 59, 0.06);
  }
`;

const MenuStatus = styled.div`
  padding: 12px 8px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.9rem;
  text-align: center;
`;

const crumbShape = PropTypes.shape({
  routeKey: PropTypes.string,
  labelKey: PropTypes.string,
  to: PropTypes.string,
  type: PropTypes.string
});

const AppBreadcrumb = ({ crumbs, params, studentName, classSwitchPath }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const menuTitle = menuTitleFromLocation(location);
  const [pendingPath, setPendingPath] = useState(null);
  const [pendingClassId, setPendingClassId] = useState(null);

  if (!crumbs || !crumbs.length) return null;

  const go = (path, classId) => {
    if (!path || path === location.pathname) return;
    if (classId) localStorage.setItem("class", classId);
    history.push({ pathname: path, state: location.state });
  };

  const requestGo = (path, classId) => {
    if (!path || path === location.pathname) return;
    if (hasUnsavedChanges()) {
      setPendingPath(path);
      setPendingClassId(classId || null);
      return;
    }
    go(path, classId);
  };

  const confirmLeave = () => {
    const path = pendingPath;
    const classId = pendingClassId;
    setPendingPath(null);
    setPendingClassId(null);
    go(path, classId);
  };

  return (
    <>
      <Nav aria-label={t("shell.breadcrumb.label")}>
        <Trail>
          {crumbs.map((crumb, index) => {
            const current = index === crumbs.length - 1;
            const key = `${crumb.type ||
              crumb.routeKey ||
              crumb.labelKey}-${index}`;

            if (crumb.type === "class") {
              return (
                <li key={key}>
                  <ClassSwitcher
                    classId={params.id}
                    current={current}
                    onOpenClass={() => requestGo(`/classes/${params.id}`)}
                    onSelect={next => {
                      if (String(next.id) === String(params.id)) return;
                      requestGo(
                        fillPath(classSwitchPath, { id: next.id }),
                        next.id
                      );
                    }}
                  />
                </li>
              );
            }

            const label =
              crumb.type === "studentName"
                ? studentName || params.studentId || ""
                : crumbLabel(t, crumb, {
                    menuTitle:
                      menuTitle && index === menuTitleCrumbIndex(crumbs)
                        ? menuTitle
                        : ""
                  });
            const pathname =
              !current && crumb.to ? fillPath(crumb.to, params) : "";

            return (
              <li key={key} aria-current={current ? "page" : undefined}>
                {pathname ? (
                  <CrumbLink
                    to={{ pathname, state: location.state }}
                    onClick={event => {
                      if (
                        event.metaKey ||
                        event.ctrlKey ||
                        event.shiftKey ||
                        event.altKey ||
                        event.button !== 0
                      ) {
                        return;
                      }
                      if (!hasUnsavedChanges()) return;
                      event.preventDefault();
                      requestGo(pathname);
                    }}
                  >
                    {label}
                  </CrumbLink>
                ) : (
                  <Current>{label}</Current>
                )}
              </li>
            );
          })}
        </Trail>
      </Nav>
      <SimpleDialog
        open={Boolean(pendingPath)}
        onClose={() => {
          setPendingPath(null);
          setPendingClassId(null);
        }}
        title={t("shell.breadcrumb.unsavedTitle")}
        message={t("shell.breadcrumb.unsavedMessage")}
        actions={(
          <>
            <Button
              color="default"
              onClick={() => {
                setPendingPath(null);
                setPendingClassId(null);
              }}
            >
              {t("common.actions.cancel")}
            </Button>
            <Button variant="contained" color="primary" onClick={confirmLeave}>
              {t("shell.breadcrumb.discard")}
            </Button>
          </>
        )}
      />
    </>
  );
};

const ClassSwitcher = ({ classId, current, onOpenClass, onSelect }) => {
  const { t } = useTranslation();
  const anchorRef = useRef(null);
  const searchTimer = useRef(null);
  const requestSeq = useRef(0);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [classes, setClasses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [className, setClassName] = useState(
    () => cachedClassName(classId) || classId || ""
  );

  useEffect(() => {
    let active = true;
    setClassName(cachedClassName(classId) || classId || "");
    ensureClassName(classId).then(name => {
      if (active) setClassName(name);
    });
    return () => {
      active = false;
    };
  }, [classId]);

  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    []
  );

  const fetchPage = async (query, skip, append) => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    setLoading(true);
    try {
      const page = await loadClasses({ search: query, skip });
      if (seq !== requestSeq.current) return;
      setTotal(page.total);
      setClasses(prev => (append ? prev.concat(page.items) : page.items));
    } catch (error) {
      if (seq !== requestSeq.current) return;
      if (!append) {
        setClasses([]);
        setTotal(0);
      }
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  const openMenu = () => {
    setOpen(true);
    setSearch("");
    fetchPage("", 0, false);
  };

  const onSearch = event => {
    const { value } = event.target;
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(
      () => fetchPage(value.trim(), 0, false),
      300
    );
  };

  const onScroll = event => {
    const node = event.currentTarget;
    const nearEnd =
      node.scrollTop + node.clientHeight >= node.scrollHeight - 24;
    if (!nearEnd || loading || classes.length >= total) return;
    fetchPage(search.trim(), classes.length, true);
  };

  const visibleClasses =
    search.trim() || classes.some(item => String(item.id) === String(classId))
      ? classes
      : [{ id: classId, name: className }, ...classes];

  let menuBody = (
    <Options
      role="listbox"
      aria-label={t("shell.route.classes")}
      onScroll={onScroll}
    >
      {visibleClasses.map(item => (
        <li key={item.id}>
          <Option
            type="button"
            role="option"
            aria-selected={String(item.id) === String(classId)}
            $selected={String(item.id) === String(classId)}
            onClick={() => {
              rememberClass(item);
              setClassName(item.name);
              setOpen(false);
              onSelect(item);
            }}
          >
            {item.name}
          </Option>
        </li>
      ))}
    </Options>
  );
  if (loading && classes.length === 0) {
    menuBody = (
      <MenuStatus>
        <CircularProgress size={22} />
      </MenuStatus>
    );
  } else if (visibleClasses.length === 0) {
    menuBody = <MenuStatus>{t("classes.list.empty")}</MenuStatus>;
  }

  const classPath = `/classes/${classId}`;

  return (
    <>
      <ClassGroup ref={anchorRef}>
        {current ? (
          <ClassName aria-current="page">{className}</ClassName>
        ) : (
          <ClassNameLink
            to={classPath}
            onClick={event => {
              if (
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey ||
                event.button !== 0
              ) {
                return;
              }
              if (!hasUnsavedChanges()) return;
              event.preventDefault();
              onOpenClass();
            }}
          >
            {className}
          </ClassNameLink>
        )}
        <ArrowButton
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t("shell.breadcrumb.changeClass")}
          onClick={() => (open ? setOpen(false) : openMenu())}
        >
          <ArrowDropDownIcon />
        </ArrowButton>
      </ClassGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Menu elevation={3}>
            <SearchField
              autoFocus
              fullWidth
              size="small"
              variant="outlined"
              value={search}
              placeholder={t("shell.breadcrumb.searchClass")}
              onChange={onSearch}
              inputProps={{ "aria-label": t("shell.breadcrumb.searchClass") }}
            />
            {menuBody}
          </Menu>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

ClassSwitcher.propTypes = {
  classId: PropTypes.string,
  current: PropTypes.bool,
  onOpenClass: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired
};

ClassSwitcher.defaultProps = {
  classId: "",
  current: false
};

AppBreadcrumb.propTypes = {
  crumbs: PropTypes.arrayOf(crumbShape),
  params: PropTypes.objectOf(PropTypes.string),
  studentName: PropTypes.string,
  classSwitchPath: PropTypes.string
};

AppBreadcrumb.defaultProps = {
  crumbs: [],
  params: {},
  studentName: "",
  classSwitchPath: ""
};

export default AppBreadcrumb;
